<template>
    <a-card :title="article_id ? $t('screenTitle.articleDetail') : $t('screenTitle.articleNew')">
        <a-skeleton :loading="isLoading" active>
            <a-form
                ref="form"
                :model="formModel"
                @finish="onFinish"
                :rules="formRule"
                :scrollToFirstError="true"
                layout="vertical"
            >
                <a-form-item :label="$t('labelText.article_title')" name="title">
                    <a-input v-model:value="formModel.title" />
                </a-form-item>

                <a-form-item :label="$t('labelText.article_slug')" name="slug">
                    <a-input v-model:value="formModel.slug" />
                </a-form-item>

                <a-form-item :label="$t('labelText.article_title_image')" name="title_image">
                    <template #help v-if="formModel.title_image.length">
                        <a-typography-paragraph copyable>{{ formModel.title_image }}</a-typography-paragraph>
                    </template>
                    <UploadImage
                        :on-change="onChangeUploadTitleImage"
                        :default-images="formModel.title_image.length ? [formModel.title_image as string] : []"
                        :max="1"
                    />
                </a-form-item>

                <a-form-item :label="$t('labelText.created_at')" name="created_at" :help="$t('message.articleCreatedAtHelp')">
                    <a-date-picker v-model:value="formModel.created_at" show-time />
                </a-form-item>

                <a-form-item :label="$t('labelText.article_content')" name="content">
                    <MarkdownEditor
                        v-model.lazy="formModel.content"
                    />
                </a-form-item>

                <a-form-item>
                    <a-button type="primary" html-type="submit" block :loading="isSaving">{{ $t('text.save') }}</a-button>
                </a-form-item>
            </a-form>
        </a-skeleton>
    </a-card>
</template>

<script lang="ts">
import { defineComponent, onMounted, reactive, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";

import { Button, Card, DatePicker, Form, Input, message, Skeleton, Typography, UploadFile } from "ant-design-vue";
import { RuleObject } from "ant-design-vue/lib/form";
import dayjs, { Dayjs } from "dayjs";

import ApiRequest from "@/utils/ApiRequest";
import { RouteName } from "@/router";
import { defineAsyncComponent, vietnameseNormalize } from "@/utils/helpers";
import UploadImage from "@/components/UploadImage.vue";

interface FormModel {
    id?: number;
    title: string;
    content: string;
    slug: string;
    title_image: string;
    created_at: Dayjs;
}

export default defineComponent({
    components: {
        ACard: Card,
        ASkeleton: Skeleton,
        AForm: Form,
        AFormItem: Form.Item,
        AInput: Input,
        AButton: Button,
        ADatePicker: DatePicker,
        ATypographyParagraph: Typography.Paragraph,

        MarkdownEditor: defineAsyncComponent(() => import("@/components/MarkdownEditor.vue")),

        UploadImage,
    },

    setup() {
        const router = useRouter();
        const route = useRoute();
        const { t } = useI18n();

        const article_id = ref(0);
        const isLoading = ref(true);
        const isSaving = ref(false);
        const form = ref<typeof Form>();

        const formModel = reactive<FormModel>({
            title: '',
            content: '',
            slug: '',
            title_image: '',
            created_at: dayjs(),
        });
        const formExtraError: Record<keyof FormModel, string> = {
            id: '',
            title: '',
            slug: '',
            content: '',
            title_image: '',
            created_at: '',
        };
        const formRule = reactive<Record<keyof FormModel, RuleObject[]>>({
            id: [],
            title: [
                {
                    required: true,
                    trigger: 'change',
                },
            ],
            title_image: [
                {
                    required: true,
                    trigger: 'change',
                },
            ],
            slug: [
                {
                    required: true,
                    trigger: 'change',
                },
                {
                    validator: (_rule, _value, callback) => callback(formExtraError.slug || undefined),
                },
            ],
            content: [
                {
                    required: true,
                    trigger: 'change',
                },
            ],
            created_at: [
                {
                    required: true,
                    trigger: 'change',
                },
            ],
        });

        const fillFormModel = (article: FormModel) => {
            formModel.id = article.id;
            formModel.title = article.title;
            formModel.created_at = dayjs(article.created_at);
            formModel.content = article.content;
            formModel.slug = article.slug;
            formModel.title_image = article.title_image;

            document.title = article.title as string;
        };

        const fetchData = () => {
            if (!article_id.value) {
                isLoading.value = false;
                return;
            }

            isLoading.value = true;

            ApiRequest.get(`/articles/${article_id.value}`).then((res) => {
                const resData = res.data;
                fillFormModel(resData.data);
            })
            .finally(() => {
                isLoading.value = false;
            });
        };

        const onFinish = (values: FormModel) => {
            isSaving.value = true;

            if (article_id.value) {
                // TODO: send only modified fields
                ApiRequest.patch(`/articles/${article_id.value}`, {
                    ...values,
                    created_at: values.created_at.format('YYYY-MM-DD HH:mm:ss'),
                })
                    .then(res => {
                        message.success(t('message.saveSuccess'));

                        fillFormModel(res.data.data)
                    })
                    .catch(err => {
                        ApiRequest.handleError(err, formExtraError, form.value);
                    })
                    .finally(() => {
                        isSaving.value = false;
                    });
            } else {
                ApiRequest.post('/articles', {
                    ...values,
                    created_at: values.created_at.format('YYYY-MM-DD HH:mm:ss'),
                })
                    .then(res => {
                        message.success(t('message.createSuccess'));

                        router.push({ name: RouteName.ARTICLES_DETAIL, params: { article_id: res.data.data.id } });
                    })
                    .catch(err => {
                        ApiRequest.handleError(err, formExtraError, form.value);
                    })
                    .finally(() => {
                        isSaving.value = false;
                    });
            }
        };

        const onChangeUploadTitleImage = (image: UploadFile) => {
            if (image.url !== formModel.title_image) {
                formModel.title_image = image.url as string;
            }
        };

        router.isReady().then(() => {
            article_id.value = parseInt(route.params.article_id as string);
        });

        watch(() => formModel.title, (newValue?: string) => {
            formModel.slug = vietnameseNormalize(newValue as string);
        });

        onMounted(() => {
            fetchData();
        });

        watch(() => article_id.value, () => {
            fetchData();
        });

        return {
            article_id,
            isLoading,
            isSaving,

            form,
            formModel,
            formRule,
            onFinish,
            onChangeUploadTitleImage,
        };
    },
})
</script>

<style scoped>

</style>
